import { useState, useMemo, useEffect } from 'react';
import { Building2, Edit, X, Loader2, Star, CheckCircle, AlertCircle } from 'lucide-react';
import {
  useExternalUserSocieties,
  useAssignSocietyToExternalUser,
  useUnassignSocietyFromExternalUser,
  useSetPrimarySocietyForExternalUser,
} from '../hooks/useExternalUserSocieties';
import { useSocietyChildren, useSocietyAncestors } from '@/features/societies/hooks/useSocieties';
import { SocietySelector } from './SocietySelector';
import { ChildSocietiesMultiSelect } from './ChildSocietiesMultiSelect';

interface Society {
  societyId: string;
  societyName: string;
  isPrimary: boolean;
  assignedAt: Date;
}

interface Notify {
  success: (msg: string) => void;
  error: (msg: string) => void;
}

interface SocietiesAssignmentCardProps {
  userId: string;
  notify: Notify;
  canEditSocieties?: boolean;
}

export const SocietiesAssignmentCard: React.FC<SocietiesAssignmentCardProps> = ({
  userId,
  notify,
  canEditSocieties = true,
}) => {
  const [showEditModal, setShowEditModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [primarySocietyId, setPrimarySocietyId] = useState('');
  const [childSocietiesIds, setChildSocietiesIds] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);

  const { data: societies = [], isLoading } = useExternalUserSocieties(userId);
  const assignMutation = useAssignSocietyToExternalUser();
  const unassignMutation = useUnassignSocietyFromExternalUser();
  const setPrimaryMutation = useSetPrimarySocietyForExternalUser();

  const { data: childSocietiesOptions = [] } = useSocietyChildren(primarySocietyId || undefined);

  const primarySociety = useMemo(() => societies.find((s: Society) => s.isPrimary), [societies]);
  const { data: ancestors = [] } = useSocietyAncestors(primarySociety?.societyId);

  const childSocieties = useMemo(() => societies.filter((s: Society) => !s.isPrimary), [societies]);

  const allowedParentIds = useMemo(() => {
    if (!primarySociety?.societyId) return undefined;
    const ids = ancestors.map((a) => a.uuid);
    ids.push(primarySociety.societyId);
    return ids;
  }, [ancestors, primarySociety]);

  useEffect(() => {
    if (showEditModal && primarySocietyId && primarySociety?.societyId !== primarySocietyId) {
      setChildSocietiesIds([]);
    }
  }, [primarySocietyId, showEditModal, primarySociety?.societyId]);

  const changes = useMemo(() => {
    const currentPrimary = societies.find((s: Society) => s.isPrimary);
    const currentSocietiesIds = societies.map((s: Society) => s.societyId);
    const newSocietiesIds = [primarySocietyId, ...childSocietiesIds];

    const primaryChanged = currentPrimary?.societyId !== primarySocietyId;
    const toAdd = newSocietiesIds.filter((id: string) => !currentSocietiesIds.includes(id));
    const toRemove = currentSocietiesIds.filter((id: string) => !newSocietiesIds.includes(id));

    const addedSocieties = toAdd.map(id => {
      if (id === primarySocietyId) {
        return { id, name: 'Nouvelle société principale' };
      }
      return { id, name: 'Société enfant' };
    });

    const removedSocieties = toRemove.map(id => {
      const society = societies.find((s: Society) => s.societyId === id);
      return { id, name: society?.societyName || 'Société inconnue' };
    });

    const newPrimaryName = currentPrimary?.societyId === primarySocietyId
      ? currentPrimary.societyName
      : 'Nouvelle société principale';

    return {
      primaryChanged,
      oldPrimaryName: currentPrimary?.societyName,
      newPrimaryName,
      added: addedSocieties,
      removed: removedSocieties,
      hasChanges: primaryChanged || toAdd.length > 0 || toRemove.length > 0,
    };
  }, [societies, primarySocietyId, childSocietiesIds]);

  const handleEdit = () => {
    const primary = societies.find((s: Society) => s.isPrimary);
    const children = societies.filter((s: Society) => !s.isPrimary).map((s: Society) => s.societyId);

    setPrimarySocietyId(primary?.societyId || '');
    setChildSocietiesIds(children);
    setShowEditModal(true);
    setError(null);
  };

  const handleSubmit = () => {
    if (!primarySocietyId) {
      notify.error('Vous devez sélectionner une société principale');
      return;
    }

    if (!changes.hasChanges) {
      setShowEditModal(false);
      return;
    }

    setShowConfirmModal(true);
  };

  const handleConfirmSave = async () => {
    if (!primarySocietyId) return;

    setIsSaving(true);
    setError(null);
    try {
      const currentSocietiesIds = societies.map((s: Society) => s.societyId);
      const newSocietiesIds = [primarySocietyId, ...childSocietiesIds];

      const toRemove = currentSocietiesIds.filter((id: string) => !newSocietiesIds.includes(id));
      const toAdd = newSocietiesIds.filter((id: string) => !currentSocietiesIds.includes(id));

      for (const societyId of toRemove) {
        await unassignMutation.mutateAsync({ userId, societyId });
      }

      for (const societyId of toAdd) {
        await assignMutation.mutateAsync({ userId, societyId });
      }

      const currentPrimary = societies.find((s: Society) => s.isPrimary);
      if (currentPrimary?.societyId !== primarySocietyId) {
        await setPrimaryMutation.mutateAsync({ userId, societyId: primarySocietyId });
      }

      notify.success('Sociétés modifiées avec succès');
      setShowEditModal(false);
      setShowConfirmModal(false);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erreur lors de la modification des sociétés');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="tw-bg-white tw-rounded-md tw-border tw-border-gray-200 tw-p-4 tw-shadow-sm">
      <div className="tw-flex tw-items-center tw-justify-between tw-mb-2.5">
        <div className="tw-flex tw-items-center tw-gap-2">
          <Building2 className="tw-w-5 tw-h-5 tw-text-affinicar-primary" />
          <h3 className="tw-text-base tw-font-semibold tw-text-gray-900 tw-mb-0">
            Sociétés
            {societies.length > 0 && (
              <span className="tw-text-xs tw-text-gray-500 tw-font-normal tw-ml-1">({societies.length})</span>
            )}
          </h3>
        </div>
        {canEditSocieties && (
          <button
            onClick={handleEdit}
            className="tw-flex tw-items-center tw-gap-1 tw-px-2 tw-py-1 tw-text-xs tw-text-affinicar-primary hover:tw-text-affinicar-secondary hover:tw-bg-affinicar-lighter tw-rounded tw-transition-colors"
          >
            <Edit className="tw-w-3 tw-h-3" />
            Modifier
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="tw-flex tw-justify-center tw-py-4">
          <Loader2 className="tw-w-5 tw-h-5 tw-animate-spin tw-text-affinicar-primary" />
        </div>
      ) : societies.length === 0 ? (
        <p className="tw-text-sm tw-text-gray-500 tw-py-2">Aucune société assignée</p>
      ) : (
        <div className="tw-space-y-2">
          {primarySociety && (
            <div className="tw-bg-yellow-50 tw-border tw-border-yellow-200 tw-rounded tw-p-2">
              <div className="tw-flex tw-items-center tw-gap-1.5">
                <Star className="tw-w-3.5 tw-h-3.5 tw-text-yellow-600 tw-flex-shrink-0" />
                <span className="tw-text-xs tw-font-semibold tw-text-gray-900 tw-truncate">
                  {primarySociety.societyName}
                </span>
              </div>
              <span className="tw-text-[10px] tw-text-gray-500 tw-ml-5">Principale</span>
            </div>
          )}

          {childSocieties.length > 0 && (
            <div className="tw-border tw-border-gray-200 tw-rounded tw-overflow-hidden">
              <button
                type="button"
                onClick={() => setIsExpanded(!isExpanded)}
                className="tw-w-full tw-flex tw-items-center tw-justify-between tw-px-2.5 tw-py-1.5 tw-bg-gray-50 hover:tw-bg-gray-100 tw-transition-colors"
              >
                <div className="tw-flex tw-items-center tw-gap-1.5">
                  <span className="tw-text-xs tw-text-gray-500">{isExpanded ? '\u25bc' : '\u25b6'}</span>
                  <span className="tw-text-xs tw-font-medium tw-text-gray-700">
                    {childSocieties.length} {childSocieties.length === 1 ? 'société enfant' : 'sociétés enfants'}
                  </span>
                </div>
              </button>

              {isExpanded && (
                <div className="tw-bg-white tw-divide-y tw-divide-gray-100 tw-max-h-48 tw-overflow-y-auto">
                  {childSocieties.map((society: Society) => (
                    <div
                      key={society.societyId}
                      className="tw-flex tw-items-center tw-gap-1.5 tw-px-2.5 tw-py-1.5 hover:tw-bg-gray-50 tw-transition-colors"
                    >
                      <Building2 className="tw-w-3 tw-h-3 tw-text-gray-400 tw-flex-shrink-0" />
                      <p className="tw-text-xs tw-text-gray-900 tw-truncate tw-mb-0">{society.societyName}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Modal de modification */}
      {showEditModal && (
        <div className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-50 tw-flex tw-items-center tw-justify-center tw-z-50">
          <div className="tw-bg-white tw-rounded-lg tw-shadow-xl tw-max-w-2xl tw-w-full tw-max-h-[90vh] tw-overflow-y-auto tw-m-4">
            <div className="tw-flex tw-items-center tw-justify-between tw-p-6 tw-border-b">
              <h3 className="tw-text-xl tw-font-semibold tw-text-gray-900">Modifier les sociétés</h3>
              <button
                onClick={() => setShowEditModal(false)}
                className="tw-text-gray-400 hover:tw-text-gray-600"
              >
                <X className="tw-w-5 tw-h-5" />
              </button>
            </div>

            <div className="tw-p-6 tw-space-y-6">
              <div>
                <SocietySelector
                  value={primarySocietyId}
                  onChange={setPrimarySocietyId}
                  allowedSocietyIds={allowedParentIds}
                />
                <p className="tw-text-xs tw-text-gray-500 tw-mt-1">
                  La société principale est la société par défaut de l'utilisateur
                </p>
              </div>

              {primarySocietyId && (
                <ChildSocietiesMultiSelect
                  societies={childSocietiesOptions}
                  selectedIds={childSocietiesIds}
                  onChange={setChildSocietiesIds}
                />
              )}
            </div>

            <div className="tw-flex tw-items-center tw-justify-end tw-gap-3 tw-p-6 tw-border-t">
              <button
                onClick={() => setShowEditModal(false)}
                className="tw-px-4 tw-py-2 tw-text-gray-700 tw-bg-gray-100 tw-rounded-md hover:tw-bg-gray-200 tw-transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleSubmit}
                disabled={!primarySocietyId}
                className="tw-px-4 tw-py-2 tw-bg-affinicar-primary tw-text-white tw-rounded-md hover:tw-bg-affinicar-secondary disabled:tw-opacity-50 tw-transition-colors"
              >
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmation */}
      {showConfirmModal && (
        <div className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-50 tw-flex tw-items-center tw-justify-center tw-z-[101] tw-p-4">
          <div className="tw-bg-white tw-rounded-lg tw-shadow-xl tw-max-w-2xl tw-w-full tw-max-h-[80vh] tw-overflow-y-auto">
            <div className="tw-flex tw-items-center tw-justify-between tw-p-6 tw-border-b tw-border-gray-200">
              <div className="tw-flex tw-items-center tw-gap-3">
                <div className="tw-w-10 tw-h-10 tw-bg-affinicar-lighter tw-rounded-full tw-flex tw-items-center tw-justify-center">
                  <CheckCircle className="tw-w-6 tw-h-6 tw-text-affinicar-primary" />
                </div>
                <h2 className="tw-text-xl tw-font-bold tw-text-gray-900">
                  Confirmer les modifications
                </h2>
              </div>
              <button
                onClick={() => setShowConfirmModal(false)}
                disabled={isSaving}
                className="tw-text-gray-400 hover:tw-text-gray-600 tw-transition-colors disabled:tw-opacity-50"
              >
                <X className="tw-w-5 tw-h-5" />
              </button>
            </div>

            <div className="tw-p-6">
              <p className="tw-text-sm tw-text-gray-600 tw-mb-4">
                Récapitulatif des modifications de sociétés :
              </p>

              <div className="tw-space-y-4">
                {changes.primaryChanged && (
                  <div className="tw-bg-affinicar-lighter tw-border tw-border-affinicar-light tw-rounded-lg tw-p-4">
                    <h3 className="tw-text-sm tw-font-semibold tw-text-affinicar-dark tw-mb-2">
                      Société primaire modifiée
                    </h3>
                    <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-3">
                      <div>
                        <p className="tw-text-xs tw-font-medium tw-text-gray-500 tw-mb-1">
                          Ancienne société primaire :
                        </p>
                        <p className="tw-text-sm tw-text-gray-700 tw-bg-white tw-p-2 tw-rounded tw-border tw-border-gray-200">
                          {changes.oldPrimaryName || '(aucune)'}
                        </p>
                      </div>
                      <div>
                        <p className="tw-text-xs tw-font-medium tw-text-affinicar-primary tw-mb-1">
                          Nouvelle société primaire :
                        </p>
                        <p className="tw-text-sm tw-text-affinicar-secondary tw-bg-affinicar-lighter tw-p-2 tw-rounded tw-border tw-border-affinicar-light tw-font-medium">
                          {changes.newPrimaryName}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {changes.added.length > 0 && (
                  <div className="tw-bg-green-50 tw-border tw-border-green-200 tw-rounded-lg tw-p-4">
                    <h3 className="tw-text-sm tw-font-semibold tw-text-green-900 tw-mb-2 tw-flex tw-items-center tw-gap-2">
                      <span className="tw-w-6 tw-h-6 tw-bg-green-100 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-text-xs">
                        +{changes.added.length}
                      </span>
                      {changes.added.length === 1 ? 'Société ajoutée' : 'Sociétés ajoutées'}
                    </h3>
                    <div className="tw-space-y-2">
                      {changes.added.map((society) => (
                        <div key={society.id} className="tw-bg-white tw-border tw-border-green-200 tw-rounded tw-p-2">
                          <p className="tw-text-sm tw-text-green-900">{society.name}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {changes.removed.length > 0 && (
                  <div className="tw-bg-red-50 tw-border tw-border-red-200 tw-rounded-lg tw-p-4">
                    <h3 className="tw-text-sm tw-font-semibold tw-text-red-900 tw-mb-2 tw-flex tw-items-center tw-gap-2">
                      <span className="tw-w-6 tw-h-6 tw-bg-red-100 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-text-xs">
                        -{changes.removed.length}
                      </span>
                      {changes.removed.length === 1 ? 'Société retirée' : 'Sociétés retirées'}
                    </h3>
                    <div className="tw-space-y-2">
                      {changes.removed.map((society) => (
                        <div key={society.id} className="tw-bg-white tw-border tw-border-red-200 tw-rounded tw-p-2">
                          <p className="tw-text-sm tw-text-red-900">{society.name}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {!changes.hasChanges && (
                  <div className="tw-bg-gray-50 tw-border tw-border-gray-200 tw-rounded-lg tw-p-4">
                    <p className="tw-text-sm tw-text-gray-600">Aucune modification détectée.</p>
                  </div>
                )}
              </div>

              {error && (
                <div className="tw-bg-red-50 tw-border tw-border-red-200 tw-rounded-lg tw-p-4 tw-mt-4">
                  <div className="tw-flex tw-items-start tw-gap-3">
                    <AlertCircle className="tw-w-5 tw-h-5 tw-text-red-600 tw-flex-shrink-0 tw-mt-0.5" />
                    <p className="tw-text-sm tw-text-red-800">{error}</p>
                  </div>
                </div>
              )}
            </div>

            <div className="tw-flex tw-justify-end tw-gap-3 tw-p-6 tw-border-t tw-border-gray-200">
              <button
                type="button"
                onClick={() => setShowConfirmModal(false)}
                disabled={isSaving}
                className="tw-px-6 tw-py-2.5 tw-bg-gray-200 tw-text-gray-700 tw-font-medium tw-rounded-lg hover:tw-bg-gray-300 tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
              >
                Retour
              </button>
              <button
                type="button"
                onClick={handleConfirmSave}
                disabled={isSaving}
                className="tw-flex tw-items-center tw-gap-2 tw-px-6 tw-py-2.5 tw-bg-affinicar-primary tw-text-white tw-font-medium tw-rounded-lg hover:tw-bg-affinicar-secondary tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
              >
                {isSaving ? (
                  <>
                    <Loader2 className="tw-w-4 tw-h-4 tw-animate-spin" />
                    Enregistrement...
                  </>
                ) : (
                  'Confirmer et enregistrer'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
